import React from "react";
import Immutable from "immutable";
import {DragDropContext, Droppable} from "react-beautiful-dnd";

import {betterMemo} from "js/common/utils/more-memo";
import {indexBy} from "js/common/utils/collections";
import Icon from "js/admin/common/icon";
import Hint from "js/admin/common/hint";

import {useCalculatedImmutableState} from "js/admin/kpis/edit-kpis/utils";
import * as Popups from "js/common/popups";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as Kpis from "js/common/kpis";
import * as Users from "js/common/users";

import DelayedTextField from "js/common/views/inputs/delayed-text-field";
import LoadingSpinner from "js/common/views/loading-spinner";
import OrderKpiDraggable from "js/admin/kpis/order-kpis/order-kpi-draggable";
import {TextButton} from "js/common/views/inputs/buttons";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";

const setOrderOnWrappedKpis = (idToWrappedKpis, orderedKpiIds) => {
  return idToWrappedKpis.withMutations(idToWrappedKpis => {
    orderedKpiIds.forEach((kpiId, index) => {
      const order = idToWrappedKpis.getIn([kpiId, "kpi", "order"]);
      const changed = order !== index;
      idToWrappedKpis
          .setIn([kpiId, "kpi", "order"], index)
          .setIn([kpiId, "positionIndex"], index)
          .updateIn([kpiId, "changed"], oldValue => oldValue || changed);
    });
  });
};

const Rows = betterMemo({debug: false, displayName: "Rows"}, (
    {
      filterText,
      idToWrappedKpi,
      onOrderChange,
      orderedKpiIds
    }) => {

  const {theme} = React.useContext(CustomThemeContext);
  const isCube19User = Users.isCube19User(Users.getCurrentUser());

  const handleDragEnd = React.useCallback(
      result => {
        if (result.destination) {
          const startIndex = result.source.index;
          const newIndex = result.destination.index;
          const changed = orderedKpiIds.get(startIndex);
          const reorderedKpiIds = orderedKpiIds.delete(startIndex).insert(newIndex, changed);

          onOrderChange(reorderedKpiIds);
        }
      }, [orderedKpiIds, onOrderChange]);

  const headingLabelStyle = {flex: "1.9 1 0%", fontSize: "11px", paddingLeft: 0, color: "#999"};

  const filteredKpiIds = React.useMemo(
      () => {
        const filterWords = Immutable.List(filterText.toLowerCase().split(" "));
        return orderedKpiIds.filter(kpiId => {
          const wrappedKpi = idToWrappedKpi.get(kpiId);

          let matchesFilter;
          if (filterText) {
            const id = isCube19User ? " " + wrappedKpi.getIn(["kpi", "id"]).toString() : "";
            const matchTerms = (wrappedKpi.getIn(["kpi", "id"]).toString() + " " + wrappedKpi.getIn(["kpi", "name"]) +
                " " +
                wrappedKpi.getIn(["kpi", "trueName"]) + id).toLowerCase();
            matchesFilter = filterWords.every(word => matchTerms.indexOf(word) !== -1);
          } else {
            matchesFilter = true;
          }

          return matchesFilter;
        });
      },
      [filterText, orderedKpiIds, idToWrappedKpi, isCube19User]);

  return (
      <div data-test-id={"order-metrics-kpi-list"}>
        <div style={{display: "flex", borderBottom: `2px solid ${theme.palette.primary.main}`, paddingBottom: 7}}>
          <span style={{...headingLabelStyle, marginLeft: 6, flex: "0.3 1 1.8%", flexShrink: 0}} />
          <span style={{...headingLabelStyle, marginLeft: 6}}>Display Name</span>
          <span style={{flex: "1 1 0%"}} />
          <span style={{flex: "0.25 1 0%"}} />
          <span style={{flex: "0.25 1 0%"}} />
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="metric-list" type="ROW" direction="vertical">
            {(droppableProvided, droppableSnapshot) => (
                <div
                    ref={droppableProvided.innerRef}
                    style={{
                      backgroundColor: droppableSnapshot.isDraggingOver ? (theme.themeId === "light"
                          ? "#d3d3d3"
                          : "#2c3e50") : "transparent",
                      position: "relative"
                    }}
                    {...droppableProvided.droppableProps}>
                  {filteredKpiIds
                      .filter(kpiId => idToWrappedKpi.has(kpiId))
                      .map(kpiId => {
                        const wrappedKpi = idToWrappedKpi.get(kpiId);
                        const kpi = wrappedKpi.get("kpi");
                        return <OrderKpiDraggable
                            theme={theme}
                            key={kpi.get("id")}
                            hasChanged={wrappedKpi.get("changed")}
                            kpi={kpi}
                            index={wrappedKpi.get("positionIndex")}
                        />;
                      })}
                  {droppableProvided.placeholder}
                </div>)}
          </Droppable>
        </DragDropContext>
      </div>);
});

const infoText = `Drag and drop Metrics into the order you want them to appear in "Priority Metrics"
                        and "Slice & Dice". It will not affect the order they are displayed within sections.`;

const sortWrappedKpis = idToWrappedKpi => {
  const wrappedKpis = idToWrappedKpi.toList();
  const visible = wrappedKpis
      .filter(wk => wk.getIn(["kpi", "visible"]))
      .sortBy(wk => wk.getIn(["kpi", "order"]))
      .map(wk => wk.getIn(["kpi", "id"]));
  const hidden = wrappedKpis
      .filter(wk => !wk.getIn(["kpi", "visible"]))
      .sortBy(wk => wk.getIn(["kpi", "name"]))
      .map(wk => wk.getIn(["kpi", "id"]));
  return visible.concat(hidden);
};

const OrderKpisApp = betterMemo({displayName: "MetricAdminPage"}, () => {
  const [filterText, setFilterText] = React.useState("");

  const [idToWrappedKpi, setIdToWrappedKpi] = React.useState(Immutable.Map());
  const [loading, setLoading] = React.useState(true);

  const {theme} = React.useContext(CustomThemeContext);

  const orderedKpiIds = useCalculatedImmutableState(() => {
    return sortWrappedKpis(idToWrappedKpi);
  }, [idToWrappedKpi]);

  const loadAndSetData = React.useCallback(() => {
    setLoading(true);
    return Kpis
        .loadEditableKpis()
        .then(kpis => {
          const wrappedKpis = kpis
              .filter(kpi => !kpi.get("deleted"))
              .map(kpi => Immutable.Map({kpi}));
          let idToWrappedKpi = indexBy(wk => wk.getIn(["kpi", "id"]), wrappedKpis);
          const sortedKpiIds = sortWrappedKpis(idToWrappedKpi);
          idToWrappedKpi = idToWrappedKpi.withMutations(idToWrappedKpi => {
            sortedKpiIds.forEach((id, index) => {
              idToWrappedKpi.setIn([id, "positionIndex"], index);
            });
          });
          setIdToWrappedKpi(idToWrappedKpi);
        })
        .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    loadAndSetData();
  }, [loadAndSetData]);


  const hasChanges = React.useMemo(
      () => !idToWrappedKpi
          .toList()
          .filter(wk => wk.get("changed"))
          .isEmpty(),
      [idToWrappedKpi]);

  const handleSaveClick = React.useCallback(() => {
    const wrappedKpisToUpsert = idToWrappedKpi.toList().filter(wk => wk.get("changed"));
    const formattedWrappedKpisToUpsert = wrappedKpisToUpsert
        .map(wk => Immutable.fromJS({config: wk.get("kpi"), columns: wk.get("columns")}));

    Kpis.submitKpis(formattedWrappedKpisToUpsert)
        .then(response => {
          const kpisUpdated = response.get("kpisUpdated");
          const kpisAndErrors = response.get("kpisAndErrors");
          const savedWrappedKpis = wrappedKpisToUpsert
              .zipWith(
                  (wrappedKpi, kpiAndError) => {
                    const kpi = kpiAndError.get("config");
                    return wrappedKpi
                        .set("kpi", kpi)
                        .delete("changed");
                  },
                  kpisAndErrors);
          const idToSavedWrappedKpis = indexBy(wk => wk.getIn(["kpi", "id"]), savedWrappedKpis);
          setIdToWrappedKpi(idToWrappedKpi.merge(idToSavedWrappedKpis));

          if (kpisUpdated) {
            KpiRepo.reload();
            Popups.changesSaved();
          } else {
            Popups.success("Changes have been submitted pending approval");
          }
        });
  }, [idToWrappedKpi]);

  const handleOrderChange = React.useCallback(orderedKpiIds => {
    setIdToWrappedKpi(setOrderOnWrappedKpis(idToWrappedKpi, orderedKpiIds));
  }, [idToWrappedKpi]);


  return (
      <div>
        <Hint style={{margin: "1rem"}}>
          <Icon icon="info" style={{color: theme.palette.hints.text}} />
          {infoText}
        </Hint>
        <div style={{display: "flex", marginBottom: "1.5rem", padding: "0 1rem"}}>
          <div style={{display: "flex", justifyContent: "center"}}>
            <div data-test-id={`search-order-metrics`}>
              <DelayedTextField
                  variant="standard"
                  style={{width: 300, marginRight: "0.5rem"}}
                  label="Search Metrics..."
                  value={filterText}
                  onChange={setFilterText} />
            </div>
          </div>
          <div
              style={{
                display: "flex",
                flexGrow: 1,
                marginLeft: "2rem",
                justifyContent: "flex-end",
                alignItems: "center"
              }}>
            <TextButton
                icon="history"
                label="Cancel"
                onClick={loadAndSetData}
                disabled={!hasChanges}
                style={{marginLeft: "0.5rem", marginRight: "0.5rem"}} />
            <TextButton
                type={(!hasChanges) ? "default" : "primary"}
                icon="floppy-o"
                label="Save"
                onClick={handleSaveClick}
                disabled={!hasChanges}
                testId="save-order-metrics"
                style={{marginLeft: "0.5rem", marginRight: "0.5rem"}} />
          </div>
        </div>

        {loading
            ? <LoadingSpinner />
            : <Rows
                filterText={filterText}
                idToWrappedKpi={idToWrappedKpi}
                onOrderChange={handleOrderChange}
                orderedKpiIds={orderedKpiIds} />}
      </div>);
});

export default OrderKpisApp;